/**
 * shared/schema/sharing.ts
 *
 * Task #1693 — Public Sharing Spaces (Foundation, Phase 1).
 *
 * Establishes the canonical persistence substrate for tenant-curated
 * public-facing "spaces" — token-addressable views over a curated subset
 * of tenant content (workers, credentials, documents, partner brand)
 * that an authenticated tenant user can publish to a public URL for
 * client/regulator consumption without requiring the recipient to hold
 * a platform login.
 *
 * Tables (all tenant-scoped except token rows which carry a denormalised
 * tenantId for fast public-token lookup without joining):
 *
 *   share_spaces             — top-level tenant-owned share container
 *   share_space_resources    — append-only join from a space to platform
 *                              entities (worker, credential, document)
 *   share_space_tokens       — opaque token rows the public URL resolves
 *                              against; carries policy snapshot + revoke
 *                              state. One space MAY have multiple tokens
 *                              (rotation, named recipients) but Phase 1
 *                              only writes one per publish.
 *   share_space_views        — append-only telemetry of public viewer
 *                              hits. Lightweight (no PII beyond IP-hash
 *                              + UA-hash); the audit-event ledger carries
 *                              the canonical share.viewed events.
 *
 * Discipline:
 *   - Every row carries `tenantId` so the existing tenant-isolation
 *     middleware can filter naturally; the public-token plane resolves
 *     the tenant via the token row before doing any further reads.
 *   - `policy` jsonb is a snapshot of the publish-time policy, NOT a
 *     live reference. Sibling tasks (versioning, watermarking, lead
 *     capture) layer additional optional fields onto the snapshot
 *     shape via permissive Zod schemas.
 *   - `status` is a discriminated union (`draft` | `published` |
 *     `revoked` | `expired`) rather than separate booleans so the
 *     state machine is auditable from a single column.
 *   - Tokens are 32-byte base64url strings stored alongside a SHA-256
 *     hash; the hash is used for lookup so a DB read does not leak the
 *     raw token (defence in depth — the public URL still carries the
 *     raw token but a DB dump alone cannot enumerate live spaces).
 */

import { sql } from "drizzle-orm";
import {
  pgTable,
  text,
  varchar,
  timestamp,
  jsonb,
  index,
  uniqueIndex,
  integer,
  type AnyPgColumn,
  boolean,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

import { tenants, users } from "./common";

// ─── Constants & shared enums ──────────────────────────────────────────

export const SHARE_SPACE_STATUSES = [
  "draft",
  "published",
  "revoked",
  "expired",
  /**
   * Task #1697 — Worker portfolios with `requireApproval = true` enter
   * `pending_approval` on publish; tenant/partner admins must approve
   * to transition to `published` (which mints the token).
   */
  "pending_approval",
] as const;
export type ShareSpaceStatus = (typeof SHARE_SPACE_STATUSES)[number];

export const SHARE_SPACE_RESOURCE_TYPES = [
  "worker",
  "credential",
  "document",
  "worker_credential",
  "worker_training",
  "worker_identity_document",
  "worker_profile",
] as const;
export type ShareSpaceResourceType =
  (typeof SHARE_SPACE_RESOURCE_TYPES)[number];

/**
 * Task #1697 — Worker Self-Service Share Spaces.
 *
 * `ownerType` distinguishes a tenant-curated space (the foundation #1693
 * surface) from a worker-owned portfolio (this task). Workers may only
 * own spaces over their own data; the policy engine enforces that.
 */
export const SHARE_SPACE_OWNER_TYPES = ["tenant", "worker"] as const;
export type ShareSpaceOwnerType = (typeof SHARE_SPACE_OWNER_TYPES)[number];

export const SHARE_SPACE_VISIBILITIES = [
  "summary",
  "full",
] as const;
export type ShareSpaceVisibility = (typeof SHARE_SPACE_VISIBILITIES)[number];

export const SHARE_QA_MODES = [
  "off",
  "questions_only",
  "questions_public_answers",
  "questions_private_answers",
] as const;
export type ShareQaMode = (typeof SHARE_QA_MODES)[number];

export const SHARE_QA_THREAD_STATUSES = [
  "open",
  "answered",
  "resolved",
  "hidden",
] as const;
export type ShareQaThreadStatus = (typeof SHARE_QA_THREAD_STATUSES)[number];

export const SHARE_QA_MODERATION_ACTION_TYPES = [
  "hide",
  "unhide",
  "delete",
  "report",
  "dismiss_report",
] as const;
export type ShareQaModerationActionType = (typeof SHARE_QA_MODERATION_ACTION_TYPES)[number];

/**
 * Publish-time policy snapshot. Fields are intentionally permissive so
 * sibling tasks (watermarking, lead capture, versioning) can layer
 * additional optional keys without a migration.
 */
export const shareSpacePolicySchema = z.object({
  visibility: z.enum(SHARE_SPACE_VISIBILITIES).default("summary"),
  requirePassword: z.boolean().default(false),
  expiresAt: z.string().datetime().nullable().optional(),
  watermark: z
    .object({
      enabled: z.boolean().default(false),
      label: z.string().max(120).optional(),
      perViewer: z.boolean().default(false),
      style: z.enum(["diagonal", "tiled", "footer"]).default("diagonal"),
      opacity: z.number().min(0).max(1).default(0.15),
      scope: z.enum(["everyone", "non_nda", "configurable"]).default("everyone"),
      includeEmail: z.boolean().default(true),
      includeTimestamp: z.boolean().default(true),
      includeIpHash: z.boolean().default(false),
      includePartnerName: z.boolean().default(true),
      customTemplate: z.string().max(500).nullable().optional(),
      blockDownload: z.boolean().default(false),
      blockPrint: z.boolean().default(false),
      disableRightClick: z.boolean().default(false),
      blurOnFocusLoss: z.boolean().default(true),
      screenshotOverlay: z.boolean().default(false),
    })
    .partial()
    .optional(),
  branding: z
    .object({
      partnerOrgId: z.string().nullable().optional(),
      accentColor: z.string().nullable().optional(),
      logoUrl: z.string().nullable().optional(),
    })
    .partial()
    .optional(),
  leadCapture: z
    .object({
      enabled: z.boolean().default(false),
      requireEmail: z.boolean().default(false),
    })
    .partial()
    .optional(),
  qaMode: z.enum(SHARE_QA_MODES).default("off"),
  defaultLocale: z.string().min(2).max(10).optional(),
  enabledLocales: z.array(z.string().min(2).max(10)).optional(),
  i18nFields: z
    .record(
      z.string(),
      z
        .object({
          name: z.string().max(200).optional(),
          description: z.string().max(2000).optional(),
        })
        .partial(),
    )
    .optional(),
  // Task #1707 — embed/OG flags layered onto the publish-time policy
  // snapshot. `embed.enabled` is the canonical owner-side toggle the
  // public embed route consults; `og.enabled` controls whether the
  // landing page emits social-card meta. Both default OFF so a Space
  // existing before #1707 keeps its previous behaviour (no embed,
  // no OG card) until the owner explicitly opts in.
  embed: z
    .object({
      enabled: z.boolean().default(false),
      // List of allowed embed origins (e.g. https://acme-contracting.com).
      // The embed endpoint sets `Content-Security-Policy: frame-ancestors`
      // from this list. Empty list with embed.enabled=true means "allow
      // any origin" — discouraged but supported for partner intranets
      // that span many sub-domains.
      allowedOrigins: z.array(z.string().trim().max(256)).max(50).default([]),
      theme: z.enum(["light", "dark", "auto"]).default("auto"),
      defaultLocale: z.string().trim().min(2).max(16).optional(),
    })
    .partial()
    .optional(),
  og: z
    .object({
      enabled: z.boolean().default(true),
      // Optional override of the title/description that ships in OG
      // meta. Falls back to the Space's name/description when null.
      title: z.string().trim().max(160).nullable().optional(),
      description: z.string().trim().max(400).nullable().optional(),
      // Override of the auto-generated headline counts string used on
      // the social card image. Owners can pin a curated tagline.
      headline: z.string().trim().max(120).nullable().optional(),
    })
    .partial()
    .optional(),
  // Task #1694 — Marketplace & Discovery. Owner-controlled flags that
  // gate whether a published space is even *eligible* for the SA-curated
  // marketplace. Default posture is opt-in (`discoverable=false`); SA
  // moderation still has the final say via the `share_marketplace_listings`
  // status column. `noindex=true` is the safe default — SA flips it on
  // approval if the tenant agrees to be indexed by external search.
  discovery: z
    .object({
      discoverable: z.boolean().default(false),
      noindex: z.boolean().default(true),
      regionScope: z.array(z.string().max(40)).max(50).optional(),
    })
    .partial()
    .optional(),
  redactedFieldClasses: z.array(z.string()).optional(),
});
export type ShareSpacePolicy = z.infer<typeof shareSpacePolicySchema>;

export const DEFAULT_SHARE_SPACE_POLICY: ShareSpacePolicy = {
  visibility: "summary",
  requirePassword: false,
  qaMode: "off",
};

// ─── Tables ────────────────────────────────────────────────────────────

export const shareSpaces = pgTable(
  "share_spaces",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    /**
     * Task #1697 — owner discriminator. `tenant` is the foundation #1693
     * surface; `worker` is the worker-owned portfolio (this task).
     * `ownerWorkerId` MUST be non-null when `ownerType = 'worker'`.
     */
    ownerType: text("owner_type").notNull().default("tenant"),
    ownerWorkerId: text("owner_worker_id"),
    /**
     * Task #1697 — when a worker publishes a portfolio while the tenant
     * has `requireApproval = true`, the space is held in `pending_approval`
     * (status column) and surfaced in the admin oversight queue. This
     * column records the partner that gates approvals (the worker's
     * primary engagement at publish time).
     */
    approvalPartnerOrgId: text("approval_partner_org_id"),
    name: text("name").notNull(),
    description: text("description"),
    status: text("status").notNull().default("draft"),
    policy: jsonb("policy")
      .$type<ShareSpacePolicy>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    publishedAt: timestamp("published_at", { withTimezone: true }),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
    expiresAt: timestamp("expires_at", { withTimezone: true }),
    createdByUserId: text("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byTenant: index("share_spaces_tenant_idx").on(t.tenantId, t.status),
    byTenantCreated: index("share_spaces_tenant_created_idx").on(
      t.tenantId,
      t.createdAt,
    ),
    byOwnerWorker: index("share_spaces_owner_worker_idx").on(
      t.tenantId,
      t.ownerType,
      t.ownerWorkerId,
    ),
  }),
);

/**
 * Task #1697 — Tenant/partner-level toggle for worker self-service
 * sharing. A row keyed on `(tenantId, partnerOrgId=null)` represents
 * the tenant-wide default; rows with a `partnerOrgId` override per
 * partner. The most-restrictive setting wins at resolve time.
 */
export const workerShareSelfServiceSettings = pgTable(
  "worker_share_self_service_settings",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    /** null = tenant-wide default; non-null = per-partner override. */
    partnerOrgId: text("partner_org_id"),
    enabled: text("enabled").notNull().default("true"),
    requireApproval: text("require_approval").notNull().default("false"),
    /**
     * Field classes that MUST be redacted in any worker-owned share
     * space (e.g. `["contact", "identity_document_numbers"]`). The
     * policy engine intersects this with the worker's own consent
     * overlay.
     */
    redactedFieldClasses: text("redacted_field_classes")
      .array()
      .notNull()
      .default(sql`ARRAY[]::text[]`),
    /** Maximum lifetime (days) a worker-owned space may be published. */
    maxLifetimeDays: integer("max_lifetime_days"),
    updatedByUserId: text("updated_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    uniqTenantDefault: uniqueIndex(
      "worker_share_settings_tenant_default_unique",
    )
      .on(t.tenantId)
      .where(sql`partner_org_id IS NULL`),
    uniqPartnerOverride: uniqueIndex("worker_share_settings_partner_unique")
      .on(t.tenantId, t.partnerOrgId)
      .where(sql`partner_org_id IS NOT NULL`),
  }),
);

export const shareSpaceResources = pgTable(
  "share_space_resources",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    resourceType: text("resource_type").notNull(),
    resourceId: text("resource_id").notNull(),
    sortOrder: integer("sort_order").notNull().default(0),
    metadata: jsonb("metadata")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    bySpace: index("share_space_resources_space_idx").on(t.spaceId, t.sortOrder),
    byResource: index("share_space_resources_resource_idx").on(
      t.tenantId,
      t.resourceType,
      t.resourceId,
    ),
    uniqPerSpace: uniqueIndex("share_space_resources_unique").on(
      t.spaceId,
      t.resourceType,
      t.resourceId,
    ),
  }),
);

export const shareSpaceTokens = pgTable(
  "share_space_tokens",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    tokenHash: text("token_hash").notNull().unique(),
    tokenPreview: text("token_preview").notNull(),
    label: text("label"),
    /**
     * GAP-063 (Task #3516) — passcodes are now bcrypt-hashed at rest.
     * On token creation with a passcode, this column receives the hash;
     * verification uses `bcrypt.compare`. Rows written before this
     * migration carry `passcodeNeedsReset = true` and the plaintext in
     * `label` so publishers can re-issue their tokens.
     */
    passcodeHash: text("passcode_hash"),
    passcodeNeedsReset: boolean("passcode_needs_reset").notNull().default(false),
    expiresAt: timestamp("expires_at", { withTimezone: true }),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
    rotatedFromTokenId: text("rotated_from_token_id"),
    createdByUserId: text("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    bySpace: index("share_space_tokens_space_idx").on(t.spaceId),
    byTenant: index("share_space_tokens_tenant_idx").on(t.tenantId),
  }),
);

export const shareSpaceViews = pgTable(
  "share_space_views",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    tokenId: text("token_id").references(() => shareSpaceTokens.id, {
      onDelete: "set null",
    }),
    /**
     * Task #1700 — version actually served on this access event. Nullable
     * for backwards compatibility with rows written before versioning
     * (treated as "served from current/v1"). FK matches migration 0126
     * (`ON DELETE SET NULL`); `shareSpaceVersions` is declared further
     * down in this file, so the lazy `() => …` reference is required.
     */
    versionId: text("version_id").references((): AnyPgColumn => shareSpaceVersions.id, {
      onDelete: "set null",
    }),
    ipHash: text("ip_hash"),
    userAgentHash: text("user_agent_hash"),
    referrer: text("referrer"),
    occurredAt: timestamp("occurred_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    bySpace: index("share_space_views_space_idx").on(t.spaceId, t.occurredAt),
    byTenantOccurred: index("share_space_views_tenant_occurred_idx").on(
      t.tenantId,
      t.occurredAt,
    ),
    byVersion: index("share_space_views_version_idx").on(t.versionId),
  }),
);

// ─── AI-Assistance Enums (Task #1705) ───────────────────────────────────

export const SHARE_AI_INVOCATION_STATUSES = [
  "pending",
  "running",
  "completed",
  "failed",
  "cancelled",
] as const;
export type ShareAiInvocationStatus =
  (typeof SHARE_AI_INVOCATION_STATUSES)[number];

export const SHARE_AI_INVOCATION_TYPES = [
  "brief_to_draft",
  "suggest_redactions",
  "risk_lint",
  "copy_rewrite",
  "copy_translate",
] as const;
export type ShareAiInvocationType =
  (typeof SHARE_AI_INVOCATION_TYPES)[number];

export const SHARE_AI_SUGGESTION_STATUSES = [
  "pending",
  "accepted",
  "rejected",
  "expired",
] as const;
export type ShareAiSuggestionStatus =
  (typeof SHARE_AI_SUGGESTION_STATUSES)[number];

export const SHARE_AI_SUGGESTION_TYPES = [
  "worker",
  "document",
  "credential",
  "summary",
  "section",
] as const;
export type ShareAiSuggestionType =
  (typeof SHARE_AI_SUGGESTION_TYPES)[number];

export const SHARE_AI_REDACTION_STATUSES = [
  "proposed",
  "accepted",
  "rejected",
  "edited",
] as const;
export type ShareAiRedactionStatus =
  (typeof SHARE_AI_REDACTION_STATUSES)[number];

export const SHARE_AI_RISK_SEVERITIES = [
  "info",
  "warning",
  "critical",
] as const;
export type ShareAiRiskSeverity =
  (typeof SHARE_AI_RISK_SEVERITIES)[number];

// ─── AI Tables (Task #1705) ─────────────────────────────────────────────

/**
 * Per-tenant AI gateway configuration. Owned by `server/services/ai-gateway.ts`
 * (`getTenantAiConfig` / `saveTenantAiConfig`). Created by migration
 * `0134_runtime_ddl_consolidation.sql` (Task #1942) — previously DDL'd at
 * runtime on first write, now migration-owned for cloud portability.
 */
export const shareAiTenantConfigs = pgTable("share_ai_tenant_configs", {
  tenantId: text("tenant_id").primaryKey(),
  config: jsonb("config")
    .$type<Record<string, unknown>>()
    .notNull()
    .default(sql`'{}'::jsonb`),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const shareAiInvocations = pgTable(
  "share_ai_invocations",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    type: text("type").notNull(),
    status: text("status").notNull().default("pending"),
    provider: text("provider"),
    model: text("model"),
    promptTokens: integer("prompt_tokens"),
    completionTokens: integer("completion_tokens"),
    costCentsMicro: integer("cost_cents_micro"),
    promptHash: text("prompt_hash"),
    input: jsonb("input")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    output: jsonb("output")
      .$type<Record<string, unknown>>()
      .default(sql`'{}'::jsonb`),
    error: text("error"),
    piiFieldsScrubbed: text("pii_fields_scrubbed").array(),
    createdByUserId: text("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    startedAt: timestamp("started_at", { withTimezone: true }),
    completedAt: timestamp("completed_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    bySpace: index("share_ai_invocations_space_idx").on(t.spaceId, t.createdAt),
    byTenant: index("share_ai_invocations_tenant_idx").on(
      t.tenantId,
      t.createdAt,
    ),
    byType: index("share_ai_invocations_type_idx").on(t.tenantId, t.type),
  }),
);

export const shareAiSuggestions = pgTable(
  "share_ai_suggestions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    invocationId: text("invocation_id")
      .notNull()
      .references(() => shareAiInvocations.id, { onDelete: "cascade" }),
    type: text("type").notNull(),
    status: text("status").notNull().default("pending"),
    resourceType: text("resource_type"),
    resourceId: text("resource_id"),
    content: jsonb("content")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    confidence: integer("confidence"),
    reason: text("reason"),
    reviewedByUserId: text("reviewed_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    bySpace: index("share_ai_suggestions_space_idx").on(t.spaceId),
    byInvocation: index("share_ai_suggestions_invocation_idx").on(
      t.invocationId,
    ),
    byStatus: index("share_ai_suggestions_status_idx").on(
      t.spaceId,
      t.status,
    ),
  }),
);

export const shareAiRedactions = pgTable(
  "share_ai_redactions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    invocationId: text("invocation_id")
      .references(() => shareAiInvocations.id, { onDelete: "set null" }),
    resourceType: text("resource_type").notNull(),
    resourceId: text("resource_id").notNull(),
    status: text("status").notNull().default("proposed"),
    piiCategory: text("pii_category").notNull(),
    regionType: text("region_type").notNull().default("text_span"),
    region: jsonb("region")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    originalPreview: text("original_preview"),
    replacementLabel: text("replacement_label"),
    confidence: integer("confidence"),
    reviewedByUserId: text("reviewed_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    bySpace: index("share_ai_redactions_space_idx").on(t.spaceId),
    byResource: index("share_ai_redactions_resource_idx").on(
      t.spaceId,
      t.resourceType,
      t.resourceId,
    ),
    byStatus: index("share_ai_redactions_status_idx").on(
      t.spaceId,
      t.status,
    ),
  }),
);

// ─── Insert schemas + types ────────────────────────────────────────────

export const insertShareSpaceSchema = createInsertSchema(shareSpaces, {
  policy: shareSpacePolicySchema,
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertShareSpace = z.infer<typeof insertShareSpaceSchema>;
export type ShareSpace = typeof shareSpaces.$inferSelect;

export const insertWorkerShareSelfServiceSettingsSchema = createInsertSchema(
  workerShareSelfServiceSettings,
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertWorkerShareSelfServiceSettings = z.infer<
  typeof insertWorkerShareSelfServiceSettingsSchema
>;
export type WorkerShareSelfServiceSettings =
  typeof workerShareSelfServiceSettings.$inferSelect;

export const insertShareSpaceResourceSchema = createInsertSchema(
  shareSpaceResources,
).omit({
  id: true,
  createdAt: true,
});
export type InsertShareSpaceResource = z.infer<
  typeof insertShareSpaceResourceSchema
>;
export type ShareSpaceResource = typeof shareSpaceResources.$inferSelect;

export const insertShareSpaceTokenSchema = createInsertSchema(
  shareSpaceTokens,
).omit({
  id: true,
  createdAt: true,
});
export type InsertShareSpaceToken = z.infer<typeof insertShareSpaceTokenSchema>;
export type ShareSpaceToken = typeof shareSpaceTokens.$inferSelect;

export const insertShareSpaceViewSchema = createInsertSchema(
  shareSpaceViews,
).omit({
  id: true,
  occurredAt: true,
});
export type InsertShareSpaceView = z.infer<typeof insertShareSpaceViewSchema>;
export type ShareSpaceView = typeof shareSpaceViews.$inferSelect;

// ─── Task #1707 — Embeds, OG / Twitter cards, link previews ────────────
//
// Two additive tables:
//   * share_embed_configs  — per-Space embed enablement, allowed origins,
//                            theme, default locale, embed-key version.
//                            One row per Space (uniqueIndex on space_id).
//   * share_og_snapshots   — per-(space, version, locale, brandingHash)
//                            cached OG image storage key + meta.
//                            Many rows per Space; cache invalidates on
//                            publish.
//
// The owner-side "Embed code" panel reads from share_embed_configs to
// generate iframe / JS snippets and to surface the rotation key. The
// public embed endpoint (`/s/:token/embed`) reads the same row to
// resolve `frame-ancestors` for the per-request CSP.

export const SHARE_EMBED_THEMES = ["light", "dark", "auto"] as const;
export type ShareEmbedTheme = (typeof SHARE_EMBED_THEMES)[number];

export const shareEmbedConfigs = pgTable(
  "share_embed_configs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    enabled: text("enabled").notNull().default("false"),
    allowedOrigins: jsonb("allowed_origins")
      .$type<string[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    theme: text("theme").notNull().default("auto"),
    defaultLocale: text("default_locale"),
    embedKey: text("embed_key").notNull(),
    embedKeyVersion: integer("embed_key_version").notNull().default(1),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    bySpace: uniqueIndex("share_embed_configs_space_unique").on(t.spaceId),
    byTenant: index("share_embed_configs_tenant_idx").on(t.tenantId),
  }),
);

export const shareOgSnapshots = pgTable(
  "share_og_snapshots",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    versionId: text("version_id").notNull(),
    locale: text("locale").notNull().default("en"),
    brandingHash: text("branding_hash").notNull(),
    title: text("title").notNull(),
    description: text("description"),
    headline: text("headline"),
    imageObjectKey: text("image_object_key").notNull(),
    imageWidth: integer("image_width").notNull().default(1200),
    imageHeight: integer("image_height").notNull().default(630),
    generatedAt: timestamp("generated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byCacheKey: uniqueIndex("share_og_snapshots_cache_key").on(
      t.spaceId,
      t.versionId,
      t.locale,
      t.brandingHash,
    ),
    byTenant: index("share_og_snapshots_tenant_idx").on(t.tenantId),
  }),
);

export const insertShareEmbedConfigSchema = createInsertSchema(
  shareEmbedConfigs,
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertShareEmbedConfig = z.infer<
  typeof insertShareEmbedConfigSchema
>;
export type ShareEmbedConfig = typeof shareEmbedConfigs.$inferSelect;

export const insertShareOgSnapshotSchema = createInsertSchema(
  shareOgSnapshots,
).omit({ id: true, generatedAt: true });
export type InsertShareOgSnapshot = z.infer<typeof insertShareOgSnapshotSchema>;
export type ShareOgSnapshot = typeof shareOgSnapshots.$inferSelect;

export const updateShareEmbedConfigSchema = z.object({
  enabled: z.boolean().optional(),
  allowedOrigins: z.array(z.string().trim().max(256)).max(50).optional(),
  theme: z.enum(SHARE_EMBED_THEMES).optional(),
  defaultLocale: z.string().trim().min(2).max(16).nullable().optional(),
});
export type UpdateShareEmbedConfig = z.infer<
  typeof updateShareEmbedConfigSchema
>;

// ─── Marketplace & Discovery (Task #1694) ──────────────────────────────
//
// Layered on top of the foundation tables above. Listing rows are
// SA-moderated (`status` column) and link 1:1 to a `share_spaces` row.
// Categories are SA-curated taxonomy. Featured rail is an explicit
// SA pick with a window. Reports capture viewer abuse / takedown
// requests for SA review.

export const SHARE_LISTING_STATUSES = [
  "pending",
  "approved",
  "rejected",
  "suspended",
  "withdrawn",
] as const;
export type ShareListingStatus = (typeof SHARE_LISTING_STATUSES)[number];

export const SHARE_REPORT_STATUSES = [
  "open",
  "actioned",
  "dismissed",
] as const;
export type ShareReportStatus = (typeof SHARE_REPORT_STATUSES)[number];

export const SHARE_REPORT_REASONS = [
  "spam",
  "misleading",
  "policy_violation",
  "stale_data",
  "other",
] as const;
export type ShareReportReason = (typeof SHARE_REPORT_REASONS)[number];

/** SA-tunable ranking weights persisted on `share_marketplace_listings.rankingSnapshot`. */
export const marketplaceRankingWeightsSchema = z.object({
  recency: z.number().min(0).max(10).default(1),
  completeness: z.number().min(0).max(10).default(1),
  trust: z.number().min(0).max(10).default(2),
  clickThrough: z.number().min(0).max(10).default(1),
  featured: z.number().min(0).max(10).default(3),
});
export type MarketplaceRankingWeights = z.infer<
  typeof marketplaceRankingWeightsSchema
>;

export const DEFAULT_MARKETPLACE_RANKING_WEIGHTS: MarketplaceRankingWeights = {
  recency: 1,
  completeness: 1,
  trust: 2,
  clickThrough: 1,
  featured: 3,
};

export const shareMarketplaceCategories = pgTable(
  "share_marketplace_categories",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    slug: text("slug").notNull().unique(),
    label: text("label").notNull(),
    description: text("description"),
    sortOrder: integer("sort_order").notNull().default(0),
    archivedAt: timestamp("archived_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    bySort: index("share_marketplace_categories_sort_idx").on(t.sortOrder),
  }),
);

export const shareMarketplaceListings = pgTable(
  "share_marketplace_listings",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    categoryId: text("category_id").references(
      () => shareMarketplaceCategories.id,
      { onDelete: "set null" },
    ),
    status: text("status").notNull().default("pending"),
    headline: text("headline").notNull(),
    summary: text("summary"),
    heroImageUrl: text("hero_image_url"),
    trade: text("trade"),
    region: text("region"),
    languages: text("languages").array(),
    verifiedWorkerCount: integer("verified_worker_count").notNull().default(0),
    completenessScore: integer("completeness_score").notNull().default(0),
    trustScore: integer("trust_score").notNull().default(0),
    impressions: integer("impressions").notNull().default(0),
    clickThroughs: integer("click_throughs").notNull().default(0),
    gatedBounces: integer("gated_bounces").notNull().default(0),
    rankingSnapshot: jsonb("ranking_snapshot")
      .$type<MarketplaceRankingWeights>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    submittedByUserId: text("submitted_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    moderatedByUserId: text("moderated_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    moderatedAt: timestamp("moderated_at", { withTimezone: true }),
    moderationNote: text("moderation_note"),
    lastValidatedAt: timestamp("last_validated_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    uniqSpace: uniqueIndex("share_marketplace_listings_space_unique").on(
      t.spaceId,
    ),
    byStatus: index("share_marketplace_listings_status_idx").on(t.status),
    byTenant: index("share_marketplace_listings_tenant_idx").on(t.tenantId),
    byCategory: index("share_marketplace_listings_category_idx").on(
      t.categoryId,
      t.status,
    ),
  }),
);

export const shareMarketplaceFeatured = pgTable(
  "share_marketplace_featured",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    listingId: text("listing_id")
      .notNull()
      .references(() => shareMarketplaceListings.id, { onDelete: "cascade" }),
    rank: integer("rank").notNull().default(0),
    startsAt: timestamp("starts_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    endsAt: timestamp("ends_at", { withTimezone: true }),
    note: text("note"),
    createdByUserId: text("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byRank: index("share_marketplace_featured_rank_idx").on(t.rank),
    byListing: index("share_marketplace_featured_listing_idx").on(t.listingId),
  }),
);

export const shareMarketplaceReports = pgTable(
  "share_marketplace_reports",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    listingId: text("listing_id")
      .notNull()
      .references(() => shareMarketplaceListings.id, { onDelete: "cascade" }),
    reason: text("reason").notNull(),
    detail: text("detail"),
    reporterIpHash: text("reporter_ip_hash"),
    reporterEmail: text("reporter_email"),
    status: text("status").notNull().default("open"),
    resolutionNote: text("resolution_note"),
    resolvedByUserId: text("resolved_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    resolvedAt: timestamp("resolved_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byListing: index("share_marketplace_reports_listing_idx").on(
      t.listingId,
      t.status,
    ),
    byStatus: index("share_marketplace_reports_status_idx").on(t.status),
  }),
);

export const insertShareMarketplaceCategorySchema = createInsertSchema(
  shareMarketplaceCategories,
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertShareMarketplaceCategory = z.infer<
  typeof insertShareMarketplaceCategorySchema
>;
export type ShareMarketplaceCategory =
  typeof shareMarketplaceCategories.$inferSelect;

export const insertShareMarketplaceListingSchema = createInsertSchema(
  shareMarketplaceListings,
  { rankingSnapshot: marketplaceRankingWeightsSchema },
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertShareMarketplaceListing = z.infer<
  typeof insertShareMarketplaceListingSchema
>;
export type ShareMarketplaceListing =
  typeof shareMarketplaceListings.$inferSelect;

export const insertShareMarketplaceFeaturedSchema = createInsertSchema(
  shareMarketplaceFeatured,
).omit({ id: true, createdAt: true });
export type InsertShareMarketplaceFeatured = z.infer<
  typeof insertShareMarketplaceFeaturedSchema
>;
export type ShareMarketplaceFeatured =
  typeof shareMarketplaceFeatured.$inferSelect;

export const insertShareMarketplaceReportSchema = createInsertSchema(
  shareMarketplaceReports,
).omit({ id: true, createdAt: true });
export type InsertShareMarketplaceReport = z.infer<
  typeof insertShareMarketplaceReportSchema
>;
export type ShareMarketplaceReport =
  typeof shareMarketplaceReports.$inferSelect;

// ─── Versioning (Task #1700) ───────────────────────────────────────────
//
// A published share space is an immutable version. Owner edits land on
// the live `share_spaces` row (the working draft); calling publish
// snapshots the current draft into a new `share_space_versions` row
// (with a deep copy of the resource list into
// `share_space_version_items`) and increments the version number.
//
// `share_space_link_version_pins` lets a token (link) be pinned to a
// specific version so a recipient continues to see the version they
// were originally sent until the owner unpins or deletes the pin.
// When no pin exists for a token, the public viewer serves the latest
// `published` version of the space.

export const shareSpaceVersions = pgTable(
  "share_space_versions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    versionNumber: integer("version_number").notNull(),
    name: text("name").notNull(),
    description: text("description"),
    policy: jsonb("policy")
      .$type<ShareSpacePolicy>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    /**
     * Structured changelog computed at publish time by diffing this
     * version against the previous one. Shape is the
     * {@link ShareSpaceVersionChangelog} produced by
     * `server/engines/share-space-differ.ts`.
     */
    changelog: jsonb("changelog")
      .$type<ShareSpaceVersionChangelog>()
      .notNull()
      .default(sql`'{"summary":[],"sections":{}}'::jsonb`),
    publishedAt: timestamp("published_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    publishedByUserId: text("published_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    /**
     * Task #1700 — snapshot of the parent space's `expires_at` at the
     * moment this version was published. Pinned tokens serve this value
     * instead of the live `share_spaces.expires_at` so historical state
     * is stable. Nullable for back-compat with versions created before
     * migration 0127 and for spaces that never had an expiry.
     */
    expiresAtSnapshot: timestamp("expires_at_snapshot", {
      withTimezone: true,
    }),
    /**
     * Task #1700 — snapshot of the parent space's `status` at the moment
     * this version was published (always `published` for normal publish
     * flows; preserved for compliance audit and SA force-pin).
     */
    statusSnapshot: text("status_snapshot"),
  },
  (t) => ({
    bySpace: index("share_space_versions_space_idx").on(
      t.spaceId,
      t.versionNumber,
    ),
    uniqVersion: uniqueIndex("share_space_versions_unique").on(
      t.spaceId,
      t.versionNumber,
    ),
    byTenant: index("share_space_versions_tenant_idx").on(t.tenantId),
  }),
);

export const shareSpaceVersionItems = pgTable(
  "share_space_version_items",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    versionId: text("version_id")
      .notNull()
      .references(() => shareSpaceVersions.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    resourceType: text("resource_type").notNull(),
    resourceId: text("resource_id").notNull(),
    sortOrder: integer("sort_order").notNull().default(0),
    metadata: jsonb("metadata")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
  },
  (t) => ({
    byVersion: index("share_space_version_items_version_idx").on(
      t.versionId,
      t.sortOrder,
    ),
    byTenant: index("share_space_version_items_tenant_idx").on(t.tenantId),
  }),
);

export const shareSpaceLinkVersionPins = pgTable(
  "share_space_link_version_pins",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    tokenId: text("token_id")
      .notNull()
      .references(() => shareSpaceTokens.id, { onDelete: "cascade" }),
    versionId: text("version_id")
      .notNull()
      .references(() => shareSpaceVersions.id, { onDelete: "cascade" }),
    pinnedByUserId: text("pinned_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    pinnedAt: timestamp("pinned_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    note: text("note"),
  },
  (t) => ({
    uniqPerToken: uniqueIndex("share_space_link_version_pins_token_unique").on(
      t.tokenId,
    ),
    bySpace: index("share_space_link_version_pins_space_idx").on(t.spaceId),
    byTenant: index("share_space_link_version_pins_tenant_idx").on(t.tenantId),
  }),
);

/**
 * Structured diff between two share-space versions. Produced by
 * `server/engines/share-space-differ.ts` and persisted on every new
 * version row as the changelog vs. the immediate predecessor.
 *
 * The shape is intentionally normalised so the UI can render it as a
 * timeline ("3 fields changed, 2 resources added, 1 removed") and as a
 * field-level side-by-side without the differ leaking field semantics
 * into the renderer.
 */
export interface ShareSpaceVersionChangelog {
  summary: Array<{
    kind:
      | "name"
      | "description"
      | "policy"
      | "branding"
      | "visibility"
      | "expiry"
      | "watermark"
      | "lead_capture"
      | "resources_added"
      | "resources_removed"
      | "resources_reordered"
      | "resource_metadata";
    detail?: string;
    count?: number;
  }>;
  sections: {
    metadata?: Array<{ field: string; before: unknown; after: unknown }>;
    policy?: Array<{ field: string; before: unknown; after: unknown }>;
    resources?: {
      added: Array<{ resourceType: string; resourceId: string }>;
      removed: Array<{ resourceType: string; resourceId: string }>;
      changed: Array<{
        resourceType: string;
        resourceId: string;
        fields: Array<{ field: string; before: unknown; after: unknown }>;
      }>;
    };
  };
}

// ─── Watermark Anti-Leak (Task #1701) ──────────────────────────────────
//
// Two additive tables:
//   * share_watermark_configs   — per-Space watermark configuration.
//                                 One row per Space (uniqueIndex on space_id).
//                                 Controls who gets watermarked, style, opacity,
//                                 anti-leak toggles (block download/print,
//                                 disable right-click, blur on focus loss).
//   * share_watermark_issuances — one row per rendered per-viewer preview.
//                                 Records the hashed viewer fingerprint so a
//                                 leaked screenshot can be traced back to the
//                                 viewer, space, and timestamp.

export const WATERMARK_STYLES = ["diagonal", "tiled", "footer"] as const;
export type WatermarkStyle = (typeof WATERMARK_STYLES)[number];

export const WATERMARK_SCOPES = ["everyone", "non_nda", "configurable"] as const;
export type WatermarkScope = (typeof WATERMARK_SCOPES)[number];

export const shareWatermarkConfigs = pgTable(
  "share_watermark_configs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    enabled: text("enabled").notNull().default("false"),
    perViewer: text("per_viewer").notNull().default("true"),
    style: text("style").notNull().default("diagonal"),
    opacity: text("opacity").notNull().default("0.15"),
    scope: text("scope").notNull().default("everyone"),
    includeEmail: text("include_email").notNull().default("true"),
    includeTimestamp: text("include_timestamp").notNull().default("true"),
    includeIpHash: text("include_ip_hash").notNull().default("false"),
    includePartnerName: text("include_partner_name").notNull().default("true"),
    customTemplate: text("custom_template"),
    blockDownload: text("block_download").notNull().default("false"),
    blockPrint: text("block_print").notNull().default("false"),
    disableRightClick: text("disable_right_click").notNull().default("false"),
    blurOnFocusLoss: text("blur_on_focus_loss").notNull().default("true"),
    screenshotOverlay: text("screenshot_overlay").notNull().default("false"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    bySpace: uniqueIndex("share_watermark_configs_space_unique").on(t.spaceId),
    byTenant: index("share_watermark_configs_tenant_idx").on(t.tenantId),
  }),
);

export const shareWatermarkIssuances = pgTable(
  "share_watermark_issuances",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    tokenId: text("token_id").references(() => shareSpaceTokens.id, {
      onDelete: "set null",
    }),
    versionId: text("version_id").references((): AnyPgColumn => shareSpaceVersions.id, {
      onDelete: "set null",
    }),
    resourceType: text("resource_type").notNull(),
    resourceId: text("resource_id").notNull(),
    viewerFingerprint: text("viewer_fingerprint").notNull(),
    viewerEmail: text("viewer_email"),
    viewerIpHash: text("viewer_ip_hash"),
    watermarkText: text("watermark_text").notNull(),
    watermarkStyle: text("watermark_style").notNull().default("diagonal"),
    partnerName: text("partner_name"),
    sessionId: text("session_id").notNull(),
    issuedAt: timestamp("issued_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    bySpace: index("share_watermark_issuances_space_idx").on(t.spaceId, t.issuedAt),
    byTenant: index("share_watermark_issuances_tenant_idx").on(t.tenantId),
    byFingerprint: index("share_watermark_issuances_fingerprint_idx").on(t.viewerFingerprint),
    bySession: index("share_watermark_issuances_session_idx").on(t.sessionId),
    byWatermarkText: index("share_watermark_issuances_watermark_text_idx").on(t.watermarkText),
  }),
);

export const insertShareWatermarkConfigSchema = createInsertSchema(
  shareWatermarkConfigs,
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertShareWatermarkConfig = z.infer<
  typeof insertShareWatermarkConfigSchema
>;
export type ShareWatermarkConfig = typeof shareWatermarkConfigs.$inferSelect;

export const insertShareWatermarkIssuanceSchema = createInsertSchema(
  shareWatermarkIssuances,
).omit({ id: true, issuedAt: true });
export type InsertShareWatermarkIssuance = z.infer<
  typeof insertShareWatermarkIssuanceSchema
>;
export type ShareWatermarkIssuance = typeof shareWatermarkIssuances.$inferSelect;

export const updateShareWatermarkConfigSchema = z.object({
  enabled: z.boolean().optional(),
  perViewer: z.boolean().optional(),
  style: z.enum(WATERMARK_STYLES).optional(),
  opacity: z.number().min(0).max(1).optional(),
  scope: z.enum(WATERMARK_SCOPES).optional(),
  includeEmail: z.boolean().optional(),
  includeTimestamp: z.boolean().optional(),
  includeIpHash: z.boolean().optional(),
  includePartnerName: z.boolean().optional(),
  customTemplate: z.string().max(500).nullable().optional(),
  blockDownload: z.boolean().optional(),
  blockPrint: z.boolean().optional(),
  disableRightClick: z.boolean().optional(),
  blurOnFocusLoss: z.boolean().optional(),
  screenshotOverlay: z.boolean().optional(),
});
export type UpdateShareWatermarkConfig = z.infer<
  typeof updateShareWatermarkConfigSchema
>;

export const insertShareSpaceVersionSchema = createInsertSchema(
  shareSpaceVersions,
  { policy: shareSpacePolicySchema },
).omit({
  id: true,
  publishedAt: true,
});
export type InsertShareSpaceVersion = z.infer<
  typeof insertShareSpaceVersionSchema
>;
export type ShareSpaceVersion = typeof shareSpaceVersions.$inferSelect;

export const insertShareSpaceVersionItemSchema = createInsertSchema(
  shareSpaceVersionItems,
).omit({
  id: true,
});
export type InsertShareSpaceVersionItem = z.infer<
  typeof insertShareSpaceVersionItemSchema
>;
export type ShareSpaceVersionItem = typeof shareSpaceVersionItems.$inferSelect;

export const insertShareSpaceLinkVersionPinSchema = createInsertSchema(
  shareSpaceLinkVersionPins,
).omit({
  id: true,
  pinnedAt: true,
});
export type InsertShareSpaceLinkVersionPin = z.infer<
  typeof insertShareSpaceLinkVersionPinSchema
>;
export type ShareSpaceLinkVersionPin =
  typeof shareSpaceLinkVersionPins.$inferSelect;

export const insertShareAiInvocationSchema = createInsertSchema(
  shareAiInvocations,
).omit({
  id: true,
  createdAt: true,
});
export type InsertShareAiInvocation = z.infer<
  typeof insertShareAiInvocationSchema
>;
export type ShareAiInvocation = typeof shareAiInvocations.$inferSelect;

export const insertShareAiSuggestionSchema = createInsertSchema(
  shareAiSuggestions,
).omit({
  id: true,
  createdAt: true,
});
export type InsertShareAiSuggestion = z.infer<
  typeof insertShareAiSuggestionSchema
>;
export type ShareAiSuggestion = typeof shareAiSuggestions.$inferSelect;

export const insertShareAiRedactionSchema = createInsertSchema(
  shareAiRedactions,
).omit({
  id: true,
  createdAt: true,
});
export type InsertShareAiRedaction = z.infer<
  typeof insertShareAiRedactionSchema
>;
export type ShareAiRedaction = typeof shareAiRedactions.$inferSelect;

// ─── Collaborative Editing (Task #1695) ─────────────────────────────
//
// Three additive tables supporting real-time multi-user editing of a
// Share Space draft:
//
//   share_space_comments     — internal comment threads on sections/items
//   share_space_suggestions  — suggested edits requiring publisher approval
//   share_space_locks        — field-level editing locks with auto-expiry
//
// All tables are tenant-scoped and private to the owning org — none of
// this data reaches the public viewer.

export const SHARE_COMMENT_STATUSES = ["active", "resolved", "deleted"] as const;
export type ShareCommentStatus = (typeof SHARE_COMMENT_STATUSES)[number];

export const SHARE_SUGGESTION_STATUSES = [
  "pending",
  "approved",
  "rejected",
  "merged",
  "withdrawn",
] as const;
export type ShareSuggestionStatus = (typeof SHARE_SUGGESTION_STATUSES)[number];

export const SHARE_LOCK_STATUSES = ["active", "released", "expired"] as const;
export type ShareLockStatus = (typeof SHARE_LOCK_STATUSES)[number];

export const shareSpaceComments = pgTable(
  "share_space_comments",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    parentId: text("parent_id"),
    targetType: text("target_type").notNull().default("space"),
    targetId: text("target_id"),
    body: text("body").notNull(),
    status: text("status").notNull().default("active"),
    authorUserId: text("author_user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    resolvedByUserId: text("resolved_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    resolvedAt: timestamp("resolved_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    bySpace: index("share_space_comments_space_idx").on(t.spaceId, t.status),
    byTarget: index("share_space_comments_target_idx").on(
      t.spaceId,
      t.targetType,
      t.targetId,
    ),
    byParent: index("share_space_comments_parent_idx").on(t.parentId),
    byTenant: index("share_space_comments_tenant_idx").on(t.tenantId),
  }),
);

export const shareSpaceSuggestions = pgTable(
  "share_space_suggestions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    targetType: text("target_type").notNull(),
    targetId: text("target_id"),
    fieldPath: text("field_path").notNull(),
    currentValue: jsonb("current_value"),
    suggestedValue: jsonb("suggested_value"),
    reason: text("reason"),
    status: text("status").notNull().default("pending"),
    authorUserId: text("author_user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    reviewedByUserId: text("reviewed_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
    reviewNote: text("review_note"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    bySpace: index("share_space_suggestions_space_idx").on(
      t.spaceId,
      t.status,
    ),
    byAuthor: index("share_space_suggestions_author_idx").on(t.authorUserId),
    byTenant: index("share_space_suggestions_tenant_idx").on(t.tenantId),
  }),
);

export const shareSpaceLocks = pgTable(
  "share_space_locks",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    sectionKey: text("section_key").notNull(),
    lockedByUserId: text("locked_by_user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    status: text("status").notNull().default("active"),
    acquiredAt: timestamp("acquired_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
    releasedAt: timestamp("released_at", { withTimezone: true }),
  },
  (t) => ({
    bySpace: index("share_space_locks_space_idx").on(t.spaceId, t.status),
    uniqActiveSection: uniqueIndex("share_space_locks_active_section").on(
      t.spaceId,
      t.sectionKey,
      t.status,
    ),
    byTenant: index("share_space_locks_tenant_idx").on(t.tenantId),
  }),
);

export const insertShareSpaceCommentSchema = createInsertSchema(
  shareSpaceComments,
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertShareSpaceComment = z.infer<
  typeof insertShareSpaceCommentSchema
>;
export type ShareSpaceComment = typeof shareSpaceComments.$inferSelect;

export const insertShareSpaceSuggestionSchema = createInsertSchema(
  shareSpaceSuggestions,
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertShareSpaceSuggestion = z.infer<
  typeof insertShareSpaceSuggestionSchema
>;
export type ShareSpaceSuggestion = typeof shareSpaceSuggestions.$inferSelect;

export const insertShareSpaceLockSchema = createInsertSchema(
  shareSpaceLocks,
).omit({ id: true, acquiredAt: true });
export type InsertShareSpaceLock = z.infer<typeof insertShareSpaceLockSchema>;
export type ShareSpaceLock = typeof shareSpaceLocks.$inferSelect;


export const SHARE_FORM_FIELD_TYPES = [
  "text",
  "email",
  "phone",
  "textarea",
  "number",
  "date",
  "select",
  "checkbox",
  "url",
] as const;
export type ShareFormFieldType = (typeof SHARE_FORM_FIELD_TYPES)[number];

export const SHARE_LEAD_STATUSES = [
  "new",
  "triaged",
  "in_progress",
  "won",
  "lost",
] as const;
export type ShareLeadStatus = (typeof SHARE_LEAD_STATUSES)[number];

export const SHARE_PROPOSAL_STATUSES = [
  "draft",
  "sent",
  "viewed",
  "signed",
  "declined",
  "revoked",
] as const;
export type ShareProposalStatus = (typeof SHARE_PROPOSAL_STATUSES)[number];

export const SHARE_OUTBOUND_ADAPTER_TYPES = [
  "webhook",
] as const;
export type ShareOutboundAdapterType =
  (typeof SHARE_OUTBOUND_ADAPTER_TYPES)[number];

export const SHARE_OUTBOUND_EVENT_TYPES = [
  "lead.created",
  "lead.updated",
  "proposal.sent",
  "proposal.signed",
] as const;
export type ShareOutboundEventType =
  (typeof SHARE_OUTBOUND_EVENT_TYPES)[number];

export const SHARE_DELIVERY_STATUSES = [
  "pending",
  "delivered",
  "failed",
  "retrying",
] as const;
export type ShareDeliveryStatus = (typeof SHARE_DELIVERY_STATUSES)[number];

export const shareRequestForms = pgTable(
  "share_request_forms",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    title: text("title").notNull(),
    description: text("description"),
    enabled: text("enabled").notNull().default("true"),
    requireAuth: text("require_auth").notNull().default("false"),
    requireEmailVerification: text("require_email_verification")
      .notNull()
      .default("false"),
    routeToUserIds: jsonb("route_to_user_ids")
      .$type<string[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    maxSubmissionsPerDay: integer("max_submissions_per_day")
      .notNull()
      .default(10),
    sortOrder: integer("sort_order").notNull().default(0),
    createdByUserId: text("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    bySpace: index("share_request_forms_space_idx").on(
      t.spaceId,
      t.sortOrder,
    ),
    byTenant: index("share_request_forms_tenant_idx").on(t.tenantId),
  }),
);

export const shareRequestFormFields = pgTable(
  "share_request_form_fields",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    formId: text("form_id")
      .notNull()
      .references(() => shareRequestForms.id, { onDelete: "cascade" }),
    fieldType: text("field_type").notNull(),
    label: text("label").notNull(),
    placeholder: text("placeholder"),
    required: text("required").notNull().default("false"),
    options: jsonb("options")
      .$type<string[]>()
      .default(sql`'[]'::jsonb`),
    validation: jsonb("validation")
      .$type<Record<string, unknown>>()
      .default(sql`'{}'::jsonb`),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byForm: index("share_request_form_fields_form_idx").on(
      t.formId,
      t.sortOrder,
    ),
  }),
);

export const shareLeads = pgTable(
  "share_leads",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    formId: text("form_id").references(() => shareRequestForms.id, {
      onDelete: "set null",
    }),
    status: text("status").notNull().default("new"),
    submitterEmail: text("submitter_email"),
    submitterName: text("submitter_name"),
    submitterCompany: text("submitter_company"),
    submitterPhone: text("submitter_phone"),
    formData: jsonb("form_data")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    assigneeUserId: text("assignee_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    internalNotes: text("internal_notes"),
    ipHash: text("ip_hash"),
    spamScore: integer("spam_score").notNull().default(0),
    tokenId: text("token_id").references(() => shareSpaceTokens.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    bySpace: index("share_leads_space_idx").on(t.spaceId, t.status),
    byTenant: index("share_leads_tenant_idx").on(t.tenantId, t.createdAt),
    byAssignee: index("share_leads_assignee_idx").on(
      t.tenantId,
      t.assigneeUserId,
    ),
  }),
);

export const shareLeadEvents = pgTable(
  "share_lead_events",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    leadId: text("lead_id")
      .notNull()
      .references(() => shareLeads.id, { onDelete: "cascade" }),
    eventType: text("event_type").notNull(),
    actorUserId: text("actor_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    previousStatus: text("previous_status"),
    newStatus: text("new_status"),
    note: text("note"),
    context: jsonb("context")
      .$type<Record<string, unknown>>()
      .default(sql`'{}'::jsonb`),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byLead: index("share_lead_events_lead_idx").on(t.leadId, t.createdAt),
  }),
);

export const shareProposals = pgTable(
  "share_proposals",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    leadId: text("lead_id").references(() => shareLeads.id, {
      onDelete: "set null",
    }),
    status: text("status").notNull().default("draft"),
    title: text("title").notNull(),
    recipientName: text("recipient_name"),
    recipientEmail: text("recipient_email"),
    recipientCompany: text("recipient_company"),
    resourceSnapshot: jsonb("resource_snapshot")
      .$type<Array<{ resourceType: string; resourceId: string; metadata?: Record<string, unknown> }>>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    pricingBlock: jsonb("pricing_block")
      .$type<{ items: Array<{ label: string; amount: number; currency?: string }>; total?: number; currency?: string; notes?: string }>()
      .default(sql`'{}'::jsonb`),
    termsBlock: text("terms_block"),
    validUntil: timestamp("valid_until", { withTimezone: true }),
    tokenHash: text("token_hash").notNull().unique(),
    tokenPreview: text("token_preview").notNull(),
    sentAt: timestamp("sent_at", { withTimezone: true }),
    viewedAt: timestamp("viewed_at", { withTimezone: true }),
    createdByUserId: text("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    bySpace: index("share_proposals_space_idx").on(t.spaceId, t.status),
    byTenant: index("share_proposals_tenant_idx").on(t.tenantId, t.createdAt),
    byLead: index("share_proposals_lead_idx").on(t.leadId),
  }),
);

export const shareProposalSignatures = pgTable(
  "share_proposal_signatures",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    proposalId: text("proposal_id")
      .notNull()
      .references(() => shareProposals.id, { onDelete: "cascade" }),
    signerName: text("signer_name").notNull(),
    signerEmail: text("signer_email").notNull(),
    signerTitle: text("signer_title"),
    signatureType: text("signature_type").notNull().default("typed"),
    signatureData: text("signature_data").notNull(),
    ipHash: text("ip_hash"),
    userAgentHash: text("user_agent_hash"),
    signedAt: timestamp("signed_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byProposal: index("share_proposal_signatures_proposal_idx").on(
      t.proposalId,
    ),
  }),
);

export const shareOutboundDestinations = pgTable(
  "share_outbound_destinations",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    adapterType: text("adapter_type").notNull().default("webhook"),
    config: jsonb("config")
      .$type<{ url: string; headers?: Record<string, string>; secret?: string }>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    events: jsonb("events")
      .$type<string[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    enabled: text("enabled").notNull().default("true"),
    createdByUserId: text("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byTenant: index("share_outbound_destinations_tenant_idx").on(t.tenantId),
  }),
);

export const shareOutboundDeliveries = pgTable(
  "share_outbound_deliveries",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    destinationId: text("destination_id")
      .notNull()
      .references(() => shareOutboundDestinations.id, { onDelete: "cascade" }),
    eventType: text("event_type").notNull(),
    payload: jsonb("payload")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    status: text("status").notNull().default("pending"),
    attempts: integer("attempts").notNull().default(0),
    maxAttempts: integer("max_attempts").notNull().default(5),
    lastAttemptAt: timestamp("last_attempt_at", { withTimezone: true }),
    nextRetryAt: timestamp("next_retry_at", { withTimezone: true }),
    lastError: text("last_error"),
    deliveredAt: timestamp("delivered_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byDestination: index("share_outbound_deliveries_dest_idx").on(
      t.destinationId,
      t.status,
    ),
    byTenant: index("share_outbound_deliveries_tenant_idx").on(
      t.tenantId,
      t.createdAt,
    ),
    byRetry: index("share_outbound_deliveries_retry_idx").on(
      t.status,
      t.nextRetryAt,
    ),
  }),
);

export const insertShareRequestFormSchema = createInsertSchema(
  shareRequestForms,
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertShareRequestForm = z.infer<
  typeof insertShareRequestFormSchema
>;
export type ShareRequestForm = typeof shareRequestForms.$inferSelect;

export const insertShareRequestFormFieldSchema = createInsertSchema(
  shareRequestFormFields,
).omit({ id: true, createdAt: true });
export type InsertShareRequestFormField = z.infer<
  typeof insertShareRequestFormFieldSchema
>;
export type ShareRequestFormField =
  typeof shareRequestFormFields.$inferSelect;

export const insertShareLeadSchema = createInsertSchema(shareLeads).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertShareLead = z.infer<typeof insertShareLeadSchema>;
export type ShareLead = typeof shareLeads.$inferSelect;

export const insertShareLeadEventSchema = createInsertSchema(
  shareLeadEvents,
).omit({ id: true, createdAt: true });
export type InsertShareLeadEvent = z.infer<typeof insertShareLeadEventSchema>;
export type ShareLeadEvent = typeof shareLeadEvents.$inferSelect;

export const insertShareProposalSchema = createInsertSchema(
  shareProposals,
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertShareProposal = z.infer<typeof insertShareProposalSchema>;
export type ShareProposal = typeof shareProposals.$inferSelect;

export const insertShareProposalSignatureSchema = createInsertSchema(
  shareProposalSignatures,
).omit({ id: true, signedAt: true });
export type InsertShareProposalSignature = z.infer<
  typeof insertShareProposalSignatureSchema
>;
export type ShareProposalSignature =
  typeof shareProposalSignatures.$inferSelect;

export const insertShareOutboundDestinationSchema = createInsertSchema(
  shareOutboundDestinations,
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertShareOutboundDestination = z.infer<
  typeof insertShareOutboundDestinationSchema
>;
export type ShareOutboundDestination =
  typeof shareOutboundDestinations.$inferSelect;

export const insertShareOutboundDeliverySchema = createInsertSchema(
  shareOutboundDeliveries,
).omit({ id: true, createdAt: true });
export type InsertShareOutboundDelivery = z.infer<
  typeof insertShareOutboundDeliverySchema
>;
export type ShareOutboundDelivery =
  typeof shareOutboundDeliveries.$inferSelect;

// ─── Task #1706 — Unified Public Portal tables ─────────────────────────

export const PUBLIC_SURFACE_TYPES = [
  "credential",
  "ptw",
  "share",
] as const;
export type PublicSurfaceType = (typeof PUBLIC_SURFACE_TYPES)[number];

export const publicSurfaceEvents = pgTable(
  "public_surface_events",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    surface: text("surface").notNull(),
    eventType: text("event_type").notNull(),
    tokenHash: text("token_hash"),
    tenantId: text("tenant_id"),
    ipHash: text("ip_hash"),
    userAgentHash: text("user_agent_hash"),
    referrer: text("referrer"),
    metadata: jsonb("metadata")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    occurredAt: timestamp("occurred_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    bySurface: index("public_surface_events_surface_idx").on(
      t.surface,
      t.occurredAt,
    ),
    byTenant: index("public_surface_events_tenant_idx").on(
      t.tenantId,
      t.occurredAt,
    ),
    byEvent: index("public_surface_events_event_idx").on(
      t.surface,
      t.eventType,
      t.occurredAt,
    ),
  }),
);

export const insertPublicSurfaceEventSchema = createInsertSchema(
  publicSurfaceEvents,
).omit({
  id: true,
  occurredAt: true,
});
export type InsertPublicSurfaceEvent = z.infer<
  typeof insertPublicSurfaceEventSchema
>;
export type PublicSurfaceEvent = typeof publicSurfaceEvents.$inferSelect;

export const publicSurfaceBranding = pgTable(
  "public_surface_branding",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    partnerOrgId: text("partner_org_id"),
    surface: text("surface"),
    logoUrl: text("logo_url"),
    accentColor: text("accent_color"),
    displayName: text("display_name"),
    footerText: text("footer_text"),
    config: jsonb("config")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byTenant: index("public_surface_branding_tenant_idx").on(t.tenantId),
    byTenantSurface: index("public_surface_branding_tenant_surface_idx").on(
      t.tenantId,
      t.surface,
    ),
  }),
);

export const insertPublicSurfaceBrandingSchema = createInsertSchema(
  publicSurfaceBranding,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertPublicSurfaceBranding = z.infer<
  typeof insertPublicSurfaceBrandingSchema
>;
export type PublicSurfaceBranding =
  typeof publicSurfaceBranding.$inferSelect;


// ─── Q&A Tables (Task #1696 — Share Spaces Public Comments & Q&A) ───

export const shareQaThreads = pgTable(
  "share_qa_threads",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    resourceType: text("resource_type"),
    resourceId: text("resource_id"),
    viewerEmailHash: text("viewer_email_hash").notNull(),
    viewerDisplayName: text("viewer_display_name").notNull(),
    questionText: text("question_text").notNull(),
    answerText: text("answer_text"),
    status: text("status").notNull().default("open"),
    isFlagged: boolean("is_flagged").notNull().default(false),
    answeredByUserId: text("answered_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    answeredAt: timestamp("answered_at", { withTimezone: true }),
    resolvedAt: timestamp("resolved_at", { withTimezone: true }),
    hiddenAt: timestamp("hidden_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    bySpace: index("share_qa_threads_space_idx").on(t.spaceId, t.status),
    byTenant: index("share_qa_threads_tenant_idx").on(t.tenantId, t.status),
    byViewer: index("share_qa_threads_viewer_idx").on(
      t.spaceId,
      t.viewerEmailHash,
    ),
    byFlagged: index("share_qa_threads_flagged_idx").on(
      t.tenantId,
      t.isFlagged,
    ),
  }),
);

export const shareQaModerationActions = pgTable(
  "share_qa_moderation_actions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    threadId: text("thread_id")
      .notNull()
      .references(() => shareQaThreads.id, { onDelete: "cascade" }),
    actionType: text("action_type").notNull(),
    reason: text("reason"),
    actorId: text("actor_id").references(() => users.id, {
      onDelete: "set null",
    }),
    actorRole: text("actor_role"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byThread: index("share_qa_mod_actions_thread_idx").on(t.threadId),
    byTenant: index("share_qa_mod_actions_tenant_idx").on(
      t.tenantId,
      t.createdAt,
    ),
  }),
);

export const shareQaFaq = pgTable(
  "share_qa_faq",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id")
      .notNull()
      .references(() => shareSpaces.id, { onDelete: "cascade" }),
    sourceThreadId: text("source_thread_id").references(
      () => shareQaThreads.id,
      { onDelete: "set null" },
    ),
    question: text("question").notNull(),
    answer: text("answer").notNull(),
    sortOrder: integer("sort_order").notNull().default(0),
    isPublished: boolean("is_published").notNull().default(true),
    createdByUserId: text("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    bySpace: index("share_qa_faq_space_idx").on(t.spaceId, t.sortOrder),
    byTenant: index("share_qa_faq_tenant_idx").on(t.tenantId),
  }),
);

// ─── Q&A Insert schemas + types ────────────────────────────────────────

export const insertShareQaThreadSchema = createInsertSchema(
  shareQaThreads,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertShareQaThread = z.infer<typeof insertShareQaThreadSchema>;
export type ShareQaThread = typeof shareQaThreads.$inferSelect;

export const insertShareQaModerationActionSchema = createInsertSchema(
  shareQaModerationActions,
).omit({
  id: true,
  createdAt: true,
});
export type InsertShareQaModerationAction = z.infer<
  typeof insertShareQaModerationActionSchema
>;
export type ShareQaModerationAction =
  typeof shareQaModerationActions.$inferSelect;

export const insertShareQaFaqSchema = createInsertSchema(shareQaFaq).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertShareQaFaq = z.infer<typeof insertShareQaFaqSchema>;
export type ShareQaFaq = typeof shareQaFaq.$inferSelect;

// ─── Task #1708 — Public Programmatic API & Bulk Management ─────────
//
// Tables:
//   * share_api_tokens       — scoped API tokens (HMAC-prefixed `nshd_share_…`)
//   * share_api_call_log     — per-call audit trail (TTL-trimmed)
//   * share_bulk_operations  — batch job tracking (dry-run / apply)
//   * share_bulk_operation_rows — per-row results within a bulk op
//   * share_api_idempotency  — idempotency-key deduplication

export const SHARE_API_TOKEN_SCOPES = [
  "spaces:read",
  "spaces:write",
  "links:rotate",
  "leads:read",
  "leads:webhook",
  "audit:read",
] as const;
export type ShareApiTokenScope = (typeof SHARE_API_TOKEN_SCOPES)[number];

export const SHARE_BULK_OP_STATUSES = [
  "pending",
  "dry_run",
  "applying",
  "completed",
  "failed",
] as const;
export type ShareBulkOpStatus = (typeof SHARE_BULK_OP_STATUSES)[number];

export const SHARE_BULK_OP_TYPES = [
  "create",
  "update",
  "publish",
  "unpublish",
  "revoke",
  "archive",
  "csv_import",
] as const;
export type ShareBulkOpType = (typeof SHARE_BULK_OP_TYPES)[number];

export const shareApiTokens = pgTable(
  "share_api_tokens",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    tokenHash: text("token_hash").notNull().unique(),
    tokenPrefix: text("token_prefix").notNull(),
    scopes: jsonb("scopes")
      .$type<ShareApiTokenScope[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    allowedIpCidrs: jsonb("allowed_ip_cidrs")
      .$type<string[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    rateLimitPerMinute: integer("rate_limit_per_minute").default(60),
    expiresAt: timestamp("expires_at", { withTimezone: true }),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
    lastUsedAt: timestamp("last_used_at", { withTimezone: true }),
    createdByUserId: text("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byTenant: index("share_api_tokens_tenant_idx").on(t.tenantId),
    byHash: uniqueIndex("share_api_tokens_hash_idx").on(t.tokenHash),
  }),
);

export const shareApiCallLog = pgTable(
  "share_api_call_log",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    tokenId: text("token_id")
      .notNull()
      .references(() => shareApiTokens.id, { onDelete: "cascade" }),
    method: text("method").notNull(),
    path: text("path").notNull(),
    statusCode: integer("status_code").notNull(),
    scopeUsed: text("scope_used"),
    ipAddress: text("ip_address"),
    idempotencyKey: text("idempotency_key"),
    durationMs: integer("duration_ms"),
    occurredAt: timestamp("occurred_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byToken: index("share_api_call_log_token_idx").on(t.tokenId, t.occurredAt),
    byTenant: index("share_api_call_log_tenant_idx").on(
      t.tenantId,
      t.occurredAt,
    ),
  }),
);

export const shareBulkOperations = pgTable(
  "share_bulk_operations",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    operationType: text("operation_type").notNull(),
    status: text("status").notNull().default("pending"),
    totalRows: integer("total_rows").notNull().default(0),
    processedRows: integer("processed_rows").notNull().default(0),
    succeededRows: integer("succeeded_rows").notNull().default(0),
    failedRows: integer("failed_rows").notNull().default(0),
    isDryRun: boolean("is_dry_run").notNull().default(true),
    inputManifestHash: text("input_manifest_hash"),
    errorSummary: jsonb("error_summary").$type<Record<string, unknown>>(),
    tokenId: text("token_id").references(() => shareApiTokens.id, {
      onDelete: "set null",
    }),
    createdByUserId: text("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    startedAt: timestamp("started_at", { withTimezone: true }),
    completedAt: timestamp("completed_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byTenant: index("share_bulk_ops_tenant_idx").on(t.tenantId, t.status),
    byToken: index("share_bulk_ops_token_idx").on(t.tokenId),
  }),
);

export const shareBulkOperationRows = pgTable(
  "share_bulk_operation_rows",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    bulkOperationId: text("bulk_operation_id")
      .notNull()
      .references(() => shareBulkOperations.id, { onDelete: "cascade" }),
    rowIndex: integer("row_index").notNull(),
    action: text("action").notNull(),
    inputData: jsonb("input_data")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    status: text("status").notNull().default("pending"),
    resultSpaceId: text("result_space_id"),
    errorMessage: text("error_message"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byOp: index("share_bulk_op_rows_op_idx").on(
      t.bulkOperationId,
      t.rowIndex,
    ),
  }),
);

export const shareApiIdempotency = pgTable(
  "share_api_idempotency",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    idempotencyKey: text("idempotency_key").notNull(),
    method: text("method").notNull(),
    path: text("path").notNull(),
    statusCode: integer("status_code").notNull(),
    responseBody: jsonb("response_body").$type<unknown>(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    expiresAt: timestamp("expires_at", { withTimezone: true })
      .notNull(),
  },
  (t) => ({
    byKey: uniqueIndex("share_api_idempotency_key_idx").on(
      t.tenantId,
      t.idempotencyKey,
    ),
  }),
);

// ─── Insert schemas + types for Task #1708 tables ───────────────────

export const insertShareApiTokenSchema = createInsertSchema(shareApiTokens).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertShareApiToken = z.infer<typeof insertShareApiTokenSchema>;
export type ShareApiToken = typeof shareApiTokens.$inferSelect;

export const insertShareApiCallLogSchema = createInsertSchema(
  shareApiCallLog,
).omit({ id: true, occurredAt: true });
export type InsertShareApiCallLog = z.infer<typeof insertShareApiCallLogSchema>;
export type ShareApiCallLog = typeof shareApiCallLog.$inferSelect;

export const insertShareBulkOperationSchema = createInsertSchema(
  shareBulkOperations,
).omit({ id: true, createdAt: true });
export type InsertShareBulkOperation = z.infer<
  typeof insertShareBulkOperationSchema
>;
export type ShareBulkOperation = typeof shareBulkOperations.$inferSelect;

export const insertShareBulkOperationRowSchema = createInsertSchema(
  shareBulkOperationRows,
).omit({ id: true, createdAt: true });
export type InsertShareBulkOperationRow = z.infer<
  typeof insertShareBulkOperationRowSchema
>;
export type ShareBulkOperationRow = typeof shareBulkOperationRows.$inferSelect;

export const insertShareApiIdempotencySchema = createInsertSchema(
  shareApiIdempotency,
).omit({ id: true, createdAt: true });
export type InsertShareApiIdempotency = z.infer<
  typeof insertShareApiIdempotencySchema
>;
export type ShareApiIdempotency = typeof shareApiIdempotency.$inferSelect;

// ─── Task #1698 — White-Label Custom Domains ───────────────────────────
//
// A tenant or partner admin can register one or more custom domains
// (`shares.acme.com`) to host the public Share viewer under their own
// brand. The lifecycle is:
//   pending → verifying → verified → issuing → active → (suspended | revoked)
// Verification is via TXT record on `_nashid-share.<domain>`; once the
// owner pushes the proof we mint an ACME order, finalise it, and persist
// the resulting cert PEMs (per-domain) in `share_domain_certificates`.
// Per-domain policy overrides (CSP allow-list, indexing, branding) live
// in `share_domain_policy_overrides` so an SA can scope a control to a
// single domain without touching the canonical Space policy.

export const SHARE_CUSTOM_DOMAIN_STATUSES = [
  "pending",
  "verifying",
  "verified",
  "issuing",
  "active",
  "suspended",
  "revoked",
  "failed",
] as const;
export type ShareCustomDomainStatus =
  (typeof SHARE_CUSTOM_DOMAIN_STATUSES)[number];

export const SHARE_DOMAIN_OWNER_TYPES = ["tenant", "partner"] as const;
export type ShareDomainOwnerType = (typeof SHARE_DOMAIN_OWNER_TYPES)[number];

export const SHARE_DOMAIN_VERIFICATION_METHODS = [
  "dns-txt",
  "http-01",
] as const;
export type ShareDomainVerificationMethod =
  (typeof SHARE_DOMAIN_VERIFICATION_METHODS)[number];

export const SHARE_DOMAIN_VERIFICATION_STATUSES = [
  "pending",
  "passed",
  "failed",
] as const;
export type ShareDomainVerificationStatus =
  (typeof SHARE_DOMAIN_VERIFICATION_STATUSES)[number];

export const SHARE_DOMAIN_CERT_STATUSES = [
  "pending",
  "issued",
  "renewing",
  "expired",
  "revoked",
  "failed",
] as const;
export type ShareDomainCertStatus =
  (typeof SHARE_DOMAIN_CERT_STATUSES)[number];

/**
 * Per-domain policy overrides. Sparse — only the keys the SA wants to
 * pin per-domain are stored; everything else falls through to the
 * Space-level policy snapshot.
 */
export const shareDomainPolicyOverrideSchema = z.object({
  noindex: z.boolean().optional(),
  hstsPreload: z.boolean().optional(),
  embedAllowedOrigins: z.array(z.string()).optional(),
  brandingMode: z.enum(["partner", "tenant", "platform"]).optional(),
  suppressPlatformBranding: z.boolean().optional(),
  cspExtraConnect: z.array(z.string()).optional(),
});
export type ShareDomainPolicyOverride = z.infer<
  typeof shareDomainPolicyOverrideSchema
>;

export const shareCustomDomains = pgTable(
  "share_custom_domains",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    ownerType: text("owner_type").notNull().default("tenant"),
    ownerPartnerOrgId: text("owner_partner_org_id"),
    domain: text("domain").notNull(),
    status: text("status").notNull().default("pending"),
    verificationToken: text("verification_token").notNull(),
    verificationHost: text("verification_host").notNull(),
    cnameTarget: text("cname_target").notNull(),
    lastVerifiedAt: timestamp("last_verified_at", { withTimezone: true }),
    activatedAt: timestamp("activated_at", { withTimezone: true }),
    suspendedAt: timestamp("suspended_at", { withTimezone: true }),
    suspendedReason: text("suspended_reason"),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
    failureReason: text("failure_reason"),
    notes: text("notes"),
    createdByUserId: text("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    approvedBySaUserId: text("approved_by_sa_user_id").references(
      () => users.id,
      { onDelete: "set null" },
    ),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    domainUniq: uniqueIndex("share_custom_domains_domain_uniq").on(t.domain),
    byTenant: index("share_custom_domains_tenant_idx").on(t.tenantId, t.status),
    byStatus: index("share_custom_domains_status_idx").on(t.status),
  }),
);

export const shareDomainVerifications = pgTable(
  "share_domain_verifications",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    domainId: text("domain_id")
      .notNull()
      .references(() => shareCustomDomains.id, { onDelete: "cascade" }),
    method: text("method").notNull().default("dns-txt"),
    status: text("status").notNull().default("pending"),
    expectedValue: text("expected_value").notNull(),
    observedValues: jsonb("observed_values")
      .$type<string[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    failureReason: text("failure_reason"),
    attemptedAt: timestamp("attempted_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byDomain: index("share_domain_verifications_domain_idx").on(
      t.domainId,
      t.attemptedAt,
    ),
  }),
);

export const shareDomainCertificates = pgTable(
  "share_domain_certificates",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    domainId: text("domain_id")
      .notNull()
      .references(() => shareCustomDomains.id, { onDelete: "cascade" }),
    status: text("status").notNull().default("pending"),
    issuer: text("issuer").notNull().default("stub-acme"),
    serial: text("serial"),
    fingerprintSha256: text("fingerprint_sha256"),
    certificatePem: text("certificate_pem"),
    chainPem: text("chain_pem"),
    privateKeyRef: text("private_key_ref"),
    notBefore: timestamp("not_before", { withTimezone: true }),
    notAfter: timestamp("not_after", { withTimezone: true }),
    issuedAt: timestamp("issued_at", { withTimezone: true }),
    renewedAt: timestamp("renewed_at", { withTimezone: true }),
    failureReason: text("failure_reason"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byDomain: index("share_domain_certificates_domain_idx").on(t.domainId),
    byStatus: index("share_domain_certificates_status_idx").on(
      t.status,
      t.notAfter,
    ),
  }),
);

export const shareDomainPolicyOverrides = pgTable(
  "share_domain_policy_overrides",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    domainId: text("domain_id")
      .notNull()
      .references(() => shareCustomDomains.id, { onDelete: "cascade" }),
    overrides: jsonb("overrides")
      .$type<ShareDomainPolicyOverride>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    setBySaUserId: text("set_by_sa_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    setBySa: text("set_by_sa").notNull().default("false"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    domainUniq: uniqueIndex("share_domain_policy_overrides_domain_uniq").on(
      t.domainId,
    ),
  }),
);

export const insertShareCustomDomainSchema = createInsertSchema(
  shareCustomDomains,
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertShareCustomDomain = z.infer<
  typeof insertShareCustomDomainSchema
>;
export type ShareCustomDomain = typeof shareCustomDomains.$inferSelect;

export const insertShareDomainVerificationSchema = createInsertSchema(
  shareDomainVerifications,
).omit({ id: true, attemptedAt: true });
export type InsertShareDomainVerification = z.infer<
  typeof insertShareDomainVerificationSchema
>;
export type ShareDomainVerification =
  typeof shareDomainVerifications.$inferSelect;

export const insertShareDomainCertificateSchema = createInsertSchema(
  shareDomainCertificates,
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertShareDomainCertificate = z.infer<
  typeof insertShareDomainCertificateSchema
>;
export type ShareDomainCertificate =
  typeof shareDomainCertificates.$inferSelect;

export const insertShareDomainPolicyOverrideSchema = createInsertSchema(
  shareDomainPolicyOverrides,
  { overrides: shareDomainPolicyOverrideSchema },
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertShareDomainPolicyOverride = z.infer<
  typeof insertShareDomainPolicyOverrideSchema
>;
export type ShareDomainPolicyOverrideRow =
  typeof shareDomainPolicyOverrides.$inferSelect;

/**
 * Validate a fully-qualified domain name. Lower-cases and rejects:
 *   - empty / over-long inputs
 *   - whitespace
 *   - leading/trailing dots
 *   - underscore-only labels (DNS allows but we don't, to keep CN sane)
 *   - apex of platform-reserved roots
 */
export const PLATFORM_RESERVED_DOMAINS: readonly string[] = [
  "replit.app",
  "repl.co",
  "replit.dev",
];

export function isPlausibleDomain(input: string): boolean {
  if (!input) return false;
  const d = input.trim().toLowerCase();
  if (d.length < 4 || d.length > 253) return false;
  if (/\s/.test(d)) return false;
  if (d.startsWith(".") || d.endsWith(".")) return false;
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(d)) {
    return false;
  }
  if (PLATFORM_RESERVED_DOMAINS.some((root) => d === root || d.endsWith(`.${root}`))) {
    return false;
  }
  return true;
}

export function buildDomainVerificationHost(domain: string): string {
  return `_nashid-share.${domain.toLowerCase()}`;
}
